/** @jsxImportSource @emotion/react */

import { useSelector } from 'react-redux';
import SupplierCardComponent from '@SupplierCard';
import AnimationWrapper from '@AnimationWrapper';
import { Box } from '@mui/material';
import {
  getLinkWithTrackingParameters,
  getSupplierNameToDisplay,
} from '../../Helpers/supplierCard';
import { hashString } from '../../Utils/utils';
import useGetExperimentName from '../../Hooks/useGetExperimentName/useGetExperimentName';
/**
 * SupplierCard Component
 *
 * Renders a card for a supplier with a logo, USPs, and a CTA button.
 * When this component appears on a page forward navigation is blocked.
 * The CTA will open the cta link, appending tracking parameters when that link is to appwiki.
 *
 */
const SupplierCard = ({ componentId }) => {
  const components = useSelector((store) => store.components);
  const component = components[`component_${componentId}`];

  const { pageUrl } = useSelector((store) => store.formConfig);

  const animationExperimentName = useGetExperimentName({
    componentId,
    componentName: 'AnimationWrapper',
  });
  const supplierCardExperimentName = useGetExperimentName({
    componentId,
    componentName: 'SupplierCard',
  });

  const additionalSettings = component?.component?.additionalSettings || {};
  const {
    usps,
    badge_label: badgeText,
    cta_text: ctaText,
    cta_link_url: ctaLinkUrl,
    cta_button_style: ctaButtonStyle,
    brandLogoUrl,
    confirmation_message: confirmationMessage,
    selected_state: selectedState,
    display_brand_name: displayBrandName,
    should_forward_all_url_parameters: shouldForwardUrlParams,
  } = additionalSettings;

  const SupplierLogo = (
    <img
      alt="supplier-logo"
      src={brandLogoUrl}
      style={{ maxWidth: '100%', height: 'auto' }}
    />
  );

  const handleClick = () => {
    if (!ctaLinkUrl) return;

    const url = getLinkWithTrackingParameters({
      targetPageUrl: ctaLinkUrl,
      parentPageUrl: pageUrl,
      shouldForwardUrlParams,
    });

    // Attempt to open the link in a new tab
    window.open(url, '_blank');
  };

  return (
    <AnimationWrapper
      effect="fadeInUp"
      delay={400}
      animationTime={600}
      experimentName={animationExperimentName}
    >
      <Box marginTop={2}>
        <SupplierCardComponent
          badgeText={badgeText}
          SupplierLogo={brandLogoUrl ? SupplierLogo : undefined}
          supplierName={getSupplierNameToDisplay({
            brandLogoUrl,
            brandName: component.component.text,
            shouldDisplayBrandName: displayBrandName,
          })}
          ctaText={confirmationMessage || ctaText || 'Visit Site'}
          id={componentId}
          includeCta={!!ctaLinkUrl}
          invertCtaColors={ctaButtonStyle === 'outlined'}
          onClick={ctaLinkUrl ? handleClick : undefined}
          selected={selectedState || false}
          includeCheckbox={false}
          experimentName={supplierCardExperimentName}
        >
          <ul>
            {usps.map((usp, index) => (
              <li key={`usp-${componentId}-${hashString(usp, index)}`}>
                {usp}
              </li>
            ))}
          </ul>
        </SupplierCardComponent>
      </Box>
    </AnimationWrapper>
  );
};

export default SupplierCard;
